/**
 * Low-battery alarm runner. Polls the device battery through @capacitor/device
 * and merges the result into the live alarm state, so a flat phone shouts on the
 * same banner, siren and buzz as every other alarm.
 *
 * Battery moves slowly, so a 30-second poll is plenty; we also re-check the
 * moment the alarm is switched on or its threshold changes, so turning it on
 * doesn't wait a poll to react. The plugin's web build reports the level where
 * the browser supports it and leaves it undefined otherwise, in which case the
 * pure evaluator simply returns null and nothing fires. On charge we stay quiet.
 *
 * Like the AIS and GNSS runners it preserves the rest of the live snapshot on
 * every write, so it never clobbers another alarm.
 */
import { useEffect } from 'react';
import { useAlarmsStore } from '@/store/alarms';
import { evaluateBatteryAlarm } from '@/safety';

const POLL_MS = 30_000;

export function BatteryAlarmRunner() {
  const enabled = useAlarmsStore((s) => s.config.lowBatteryEnabled);
  const thresholdPct = useAlarmsStore((s) => s.config.lowBatteryPct);

  useEffect(() => {
    let cancelled = false;

    const clearIfSet = () => {
      const live = useAlarmsStore.getState().live;
      if (live.lowBattery != null) useAlarmsStore.getState().setLive({ ...live, lowBattery: null });
    };

    if (!enabled) {
      clearIfSet();
      return;
    }

    const poll = async () => {
      let levelPct: number | null = null;
      let charging = false;
      try {
        const { Device } = await import('@capacitor/device');
        const info = await Device.getBatteryInfo();
        levelPct = typeof info.batteryLevel === 'number' ? Math.round(info.batteryLevel * 100) : null;
        charging = info.isCharging === true;
      } catch {
        // Plugin missing or unavailable (e.g. a browser with no battery API): we
        // simply can't read it, so the alarm stays quiet rather than guessing.
        levelPct = null;
      }
      if (cancelled) return;
      const res = evaluateBatteryAlarm({
        enabled: useAlarmsStore.getState().config.lowBatteryEnabled,
        levelPct,
        charging,
        thresholdPct: useAlarmsStore.getState().config.lowBatteryPct,
      });
      const live = useAlarmsStore.getState().live;
      if (JSON.stringify(live.lowBattery) === JSON.stringify(res)) return;
      useAlarmsStore.getState().setLive({ ...live, lowBattery: res });
    };

    void poll();
    const id = window.setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled, thresholdPct]);

  return null;
}
